/**
 * Cookie unlock for gated content downloads embedded in rich text.
 * Key is derived from the Sanity form id + download URL so the same form
 * on different assets (or pages) does not unlock incorrectly.
 */

export const GATED_DOWNLOAD_COOKIE_PREFIX = 'trscnd_gdl_';

/** Remember unlock for a year so return visits skip the email gate. */
export const GATED_DOWNLOAD_COOKIE_EXPIRATION_DAYS = 365;

/**
 * Query param that clears gated-download unlock cookies so the form shows again.
 * Example: `/blog/my-post?resetGatedDownload`
 */
export const GATED_DOWNLOAD_RESET_QUERY_PARAM = 'resetGatedDownload';

function hashKey(value: string): string {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash).toString(36);
}

export function gatedDownloadCookieName(
  formId: string,
  downloadUrl: string,
): string {
  return `${GATED_DOWNLOAD_COOKIE_PREFIX}${hashKey(`${formId}|${downloadUrl}`)}`;
}

function cookieExpiresAt(days: number): Date {
  const expires = new Date();
  expires.setDate(expires.getDate() + days);
  return expires;
}

function expireCookie(name: string): void {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax`;
}

export function hasGatedDownloadCookie(
  formId: string,
  downloadUrl: string,
): boolean {
  if (typeof document === 'undefined') {
    return false;
  }
  const name = gatedDownloadCookieName(formId, downloadUrl);
  return document.cookie
    .split('; ')
    .some((part) => part.startsWith(`${name}=`));
}

export function setGatedDownloadCookie(
  formId: string,
  downloadUrl: string,
): void {
  if (typeof document === 'undefined') {
    return;
  }
  const name = gatedDownloadCookieName(formId, downloadUrl);
  const expires = cookieExpiresAt(GATED_DOWNLOAD_COOKIE_EXPIRATION_DAYS);
  document.cookie = `${name}=1; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
}

export function clearGatedDownloadCookie(
  formId: string,
  downloadUrl: string,
): void {
  if (typeof document === 'undefined') {
    return;
  }
  expireCookie(gatedDownloadCookieName(formId, downloadUrl));
}

/** Clears every gated-download unlock cookie on this origin. */
export function clearAllGatedDownloadCookies(): void {
  if (typeof document === 'undefined') {
    return;
  }
  for (const part of document.cookie.split('; ')) {
    const name = part.split('=')[0];
    if (name?.startsWith(GATED_DOWNLOAD_COOKIE_PREFIX)) {
      expireCookie(name);
    }
  }
}
